<template>
  <div
    :class="[$style.base, { [$style.fullHeight]: fullHeight, [$style.fullWidth]: fullWidth, [$style.grow]: grow }]"
  >
    <div
      :class="[
        $style.inner,
        {
          [$style.gap025]: gap === 0.25,
          [$style.gap05]: gap === 0.5,
          [$style.gap075]: gap === 0.75,
          [$style.gap1]: gap === 1,
          [$style.gap15]: gap === 1.5,
          [$style.gap2]: gap === 2,
          [$style.padding05]: padding === 0.5,
          [$style.padding1]: padding === 1 || padding === true,
          [$style.padding15]: padding === 1.5,
          [$style.padding2]: padding === 2,
          [$style.wrap]: wrap,
          [$style.justifyBetween]: justify === 'between',
          [$style.justifyAround]: justify === 'around',
          [$style.justifyCenter]: justify === 'center',
          [$style.justifyEnd]: justify === 'end',
          [$style.justifyEvenly]: justify === 'evenly',
          [$style.alignCenter]: align === 'center',
          [$style.alignStart]: align === 'start',
          [$style.alignEnd]: align === 'end',
          [$style.alignStretch]: align === 'stretch',
          [$style.directionColumn]: direction === 'column',
          [$style.directionColumnReverse]: direction === 'column-reverse',
          [$style.equalWidth]: equalWidth,
        }
      ]"
    >
      <slot />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
withDefaults(
  defineProps<{
    align?: 'center' | 'end' | 'start' | 'stretch';
    direction?: 'row' | 'column' | 'column-reverse';
    justify?: 'between' | 'around' | 'center' | 'end' | 'evenly';
    gap?: 0 | 0.25 | 0.5 | 0.75 | 1 | 1.5 | 2;
    padding?: 0 | 0.5 | 1 | 1.5 | 2 | boolean;
    wrap?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    equalWidth?: boolean;
    grow?: boolean;
  }>(),
  {
    direction: 'row',
    gap: 1,
    padding: 0,
    wrap: false,
    fullHeight: false,
    fullWidth: false,
    equalWidth: false,
    grow: false,
  },
);
</script>

<style
  lang="scss"
  module
>
.base {
  &:has(> div:empty) {
    display: none;
  }

  &.fullHeight {
    height: 100%;
  }

  &.fullWidth {
    width: 100%;
  }

  &.grow {
    flex-grow: 1;
  }
}

.inner {
  display: flex;

  &.gap025 {
    gap: $spacing * 0.25;
  }

  &.gap05 {
    gap: $spacing * 0.5;
  }

  &.gap075 {
    gap: $spacing * 0.75;
  }

  &.gap1 {
    gap: $spacing;
  }

  &.gap15 {
    gap: $spacing * 1.5;
  }

  &.gap2 {
    gap: $spacing * 2;
  }

  &.wrap {
    flex-wrap: wrap;
  }

  &.justifyBetween {
    justify-content: space-between;
  }

  &.justifyAround {
    justify-content: space-around;
  }

  &.justifyCenter {
    justify-content: center;
  }

  &.justifyEnd {
    justify-content: flex-end;
  }

  &.justifyEvenly {
    justify-content: space-evenly;
  }

  &.alignCenter {
    align-items: center;
  }

  &.alignStart {
    align-items: start;
  }

  &.alignEnd {
    align-items: end;
  }

  &.alignStretch {
    align-items: stretch;
  }

  &.directionColumn {
    flex-direction: column;
  }

  &.directionColumnReverse {
    flex-direction: column-reverse;
  }
  
  &.padding05 {
    padding: $spacing * 0.5;
  }

  &.padding1 {
    padding: $spacing;
  }

  &.padding15 {
    padding: $spacing * 1.5;
  }

  &.padding2 {
    padding: $spacing * 2;
  }

  &.equalWidth {
    & > * {
      flex: 1;
      min-width: 0;
      width: 100%;
    }
  }

  .base.fullHeight & {
    height: 100%;
  }

  .base.fullWidth & {
    width: 100%;
  }

  &:empty {
    display: none;
  }
}
</style>
